import { useState } from "react";
import { Button, Field, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useUpdateSmartLink, useReplaceDspLinks, problemType } from "../../app/hooks/useRelease.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { Modal } from "./Modal.js";
import { DSPS } from "./dsp.js";
import styles from "./AssetEditorModal.module.scss";
import own from "./SmartLinkEditorModal.module.scss";

type DspLinkDto = components["schemas"]["DspLinkDto"];

// Edit smart link (shot-12) — the slug + the per-DSP URLs in one modal. The two are SEPARATE endpoints
// that each bump the release e_ver, so a naive "one version for both" would self-409; instead Save runs them
// serially, slug FIRST (the fallible one — 409 slug-taken), threading the NEW version it returns into the
// dsp-links replace. Both are If-Match writes; a failure surfaces inline and the composed record repaints from
// REST on success (the phone-mockup preview + public buttons update).

export interface SmartLinkEditorModalProps {
  releaseId: string;
  version: number;
  currentSlug: string;
  bandSlug: string;
  currentLinks: DspLinkDto[];
  onClose: () => void;
}

export function SmartLinkEditorModal({ releaseId, version, currentSlug, bandSlug, currentLinks, onClose }: SmartLinkEditorModalProps) {
  // silent: the modal renders its own inline error (incl. the mapped slug-taken message), so no global toast.
  const updateSlug = useUpdateSmartLink(releaseId, { silent: true });
  const replaceDsp = useReplaceDspLinks(releaseId, { silent: true });

  const initialUrls = Object.fromEntries(currentLinks.map((l) => [l.dsp ?? "", l.url ?? ""]));
  const [slug, setSlug] = useState(currentSlug);
  const [urls, setUrls] = useState<Record<string, string>>(initialUrls);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slugDirty = slug.trim() !== currentSlug;
  const dspDirty = DSPS.some((d) => (urls[d.key] ?? "").trim() !== (initialUrls[d.key] ?? ""));
  const dirty = slugDirty || dspDirty;

  // Lock dismissal during the serialized slug→dsp save so Escape/scrim can't close between the two writes.
  function handleClose() {
    if (saving) return;
    onClose();
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      let v = version;
      if (slugDirty) {
        const r = await updateSlug.mutateAsync({ version: v, slug: slug.trim() });
        v = r.version ?? v;
      }
      if (dspDirty) {
        const links = DSPS.map((d, i) => ({ dsp: d.key, url: (urls[d.key] ?? "").trim(), position: i })).filter((l) => l.url);
        await replaceDsp.mutateAsync({ version: v, body: { links } });
      }
      onClose();
    } catch (e) {
      setError(problemType(e) === "slug-taken" ? "That slug is already taken — try another." : errorMessage(e, "Couldn't save the smart link"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit smart link" onClose={handleClose}>
      <div className={styles.body}>
        <div>
          <Field
            label="Public slug"
            placeholder="your-song-title"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={saving}
            hint={`The public URL is /${bandSlug || "band"}/${slug.trim() || "your-song"}`}
          />
        </div>

        <div className={own.dspList}>
          <p className={styles.label}>Streaming links</p>
          {DSPS.filter((d) => d.key !== "other").map((d) => (
            <Field
              key={d.key}
              label={d.label}
              placeholder={`${d.label} link (optional)`}
              value={urls[d.key] ?? ""}
              onChange={(e) => setUrls((prev) => ({ ...prev, [d.key]: e.target.value }))}
              disabled={saving}
            />
          ))}
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <div className={styles.actionsRight}>
            <DialogCancel size="sm" disabled={saving} />
            <Button size="sm" onClick={save} loading={saving} disabled={!dirty}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
